"use client";

import { SocialPostCard, SocialPostImage, SocialPostLock, SocialPostMedia, SocialPostVideo } from "@webdo/community-ui";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePortalTranslations } from "../i18n/client";
import { usePortalPreferences } from "./PortalPreferences";
import { postMediaUrl, postRequest, safePostVideoUrl, type PortalPost, type PostFeed } from "../lib/portalPosts";
import styles from "./PortalPosts.module.css";
import { CommunityEngagement } from "./CommunityEngagement";

function PostCard({ post, authenticated }: { post: PortalPost; authenticated: boolean }) {
  const t = usePortalTranslations();
  const { locale } = usePortalPreferences();
  const video = safePostVideoUrl(post.videoUrl);
  const portalHref = `/${encodeURIComponent(post.portalSlug)}`;
  return <SocialPostCard id={`post-${post.uuid}`}
    authorName={post.portalName}
    author={<Link href={portalHref} prefetch={false}>{post.portalName}</Link>}
    metadata={<><span>{t(`posts.audience.${post.audience}`)}</span>{post.publishedAt ? <><span aria-hidden="true"> · </span><time dateTime={post.publishedAt}>{new Intl.DateTimeFormat(locale === "cs" ? "cs-CZ" : "en-GB", { dateStyle: "medium" }).format(new Date(post.publishedAt))}</time></> : null}</>}
    title={post.title}
    teaser={post.teaser}
    cover={post.cover ? <SocialPostImage src={postMediaUrl(post.portalSlug, post.cover.uuid, post.uuid)} alt={t("posts.coverAlt", { title: post.title })} /> : null}
    locked={post.locked ? <SocialPostLock title={t("posts.locked")} hint={t(post.audience === "members" ? "posts.memberHint" : "posts.planHint")} action={<Link className={styles.primary} href={`${portalHref}/access`} prefetch={false}>{t("posts.unlock")}</Link>} /> : null}
    body={post.body}
    media={<>{video ? <SocialPostVideo src={video} title={t("posts.videoTitle", { title: post.title })} /> : null}<SocialPostMedia items={post.media.map((item) => ({ id: item.uuid, name: item.name, mimeType: item.mimeType, size: item.size, url: postMediaUrl(post.portalSlug, item.uuid, post.uuid) }))} /></>}
    footer={<CommunityEngagement targetType="post" targetUuid={post.uuid} authenticated={authenticated} />}
  />;
}

export function PortalPostFeed({ initialFeed, authenticated }: { initialFeed: PostFeed | null; authenticated: boolean }) {
  const t = usePortalTranslations();
  const [feed, setFeed] = useState(initialFeed);
  const [error, setError] = useState(initialFeed === null);
  const [loading, setLoading] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);
  const pending = useRef(false);
  const generation = useRef(0);

  const load = useCallback(async (reset = false) => {
    if (!reset && (pending.current || feed?.nextOffset === null)) return;
    const requestGeneration = reset ? ++generation.current : generation.current;
    pending.current = true; setLoading(true); setError(false);
    try {
      const result = await postRequest<PostFeed>(`/api/client-portals/_directory/posts?offset=${reset ? 0 : feed?.nextOffset ?? 0}`);
      if (requestGeneration !== generation.current) return;
      setFeed((current) => ({ ...result, items: reset ? result.items : [...(current?.items ?? []), ...result.items.filter((post) => !current?.items.some((item) => item.uuid === post.uuid))] }));
    } catch { if (requestGeneration === generation.current) setError(true); }
    finally { if (requestGeneration === generation.current) { pending.current = false; setLoading(false); } }
  }, [feed?.nextOffset]);

  useEffect(() => {
    if (!sentinel.current || loading || error || !feed || feed.nextOffset === null) return;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) void load(); }, { rootMargin: "240px" });
    observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [feed, loading, error, load]);

  return <section className={styles.feed} aria-label={t("posts.title")}>
    <h2 className={styles.feedHeading}>{t("posts.title")}</h2>
    <div className={styles.stream}>{feed?.items.map((post) => <PostCard key={post.uuid} post={post} authenticated={authenticated} />)}</div>
    {!feed?.items.length && !loading && !error ? <p className={styles.empty}>{t("posts.empty")}</p> : null}
    <div ref={sentinel} className={styles.loadMore}>
      {error ? <div role="alert"><p>{t("posts.error")}</p><button type="button" className={styles.secondary} onClick={() => void load(!feed)}>{t("posts.retry")}</button></div> : loading ? <p role="status">{t("posts.loading")}</p> : feed?.nextOffset !== null ? <button type="button" className={styles.secondary} onClick={() => void load()}>{t("posts.more")}</button> : feed.items.length ? <p>{t("posts.end")}</p> : null}
    </div>
  </section>;
}
